// Define the base type for the options argument (SIMPLE, NON-GENERIC)
export type FindOptions = {
  deepCompare?: boolean;
  useObjectKey?: string;
}

// Define the generic options type for callers like toggleArray
// This is the type that *should* be used to make call sites safe.
export type GenericFindOptions<T extends Record<string, any>> = {
  deepCompare?: boolean;
  useObjectKey?: keyof T & string;
}

// Overload 1: For comparing objects using a specified key (`useObjectKey`)
// T must extend an object type where V is the type of the key value being compared.
function findArrayIndex<T extends Record<K, V>, K extends string, V>(
  array: T[],
  value: T,
  options: FindOptions & { useObjectKey: K } // Uses simple FindOptions
): number;

// Overload 2: For deep comparison (comparing stringified objects)
function findArrayIndex<T extends object>(
  array: T[],
  value: T,
  options: FindOptions & { deepCompare: true }
): number;

// Overload 3: For simple comparison (primitives or simple object references)
function findArrayIndex<T>(
  array: T[],
  value: T,
  options?: FindOptions // Uses simple FindOptions
): number;

// FUNCTION IMPLEMENTATION
// The implementation uses the most general types from the overloads.
function findArrayIndex(
  array: any[],
  value: any,
  options: FindOptions = {}
): number {
  const { deepCompare, useObjectKey } = options;

  // 1. Compare by Object Key
  if (useObjectKey) {
    // FIX: Add necessary type guards to ensure item and value are objects
    return array.findIndex(item => 
      item && typeof item === 'object' && 
      value && typeof value === 'object' && 
      item[useObjectKey] === value[useObjectKey]
    );
  }

  // 2. Deep Compare
  if (deepCompare) {
    const valueString = JSON.stringify(value);
    
    // FIX: Check for item to be an object before stringifying
    return array.findIndex(item => 
      item && typeof item === 'object' && 
      JSON.stringify(item) === valueString
    );
  }

  // 3. Simple Comparison
  return array.findIndex(item => item === value);
}

export default findArrayIndex;